import axios from "axios";
import React from "react";

import { SpinnerComponent } from "./commonComponent/SpinnerComponent";
import { FormPreviewComponent } from "./designComponent/FormPreviewComponent";
import { APIKey, ProgramContainer } from "./entities/formTemplateDataType";
import { getPageForSuccessfulProcess,
				 getPageForUnsuccessfulProcess,
				 getTransactionId,
				 isProcessSuccessful,
				 isRedirectionRequired, 
				 populateApiRequest} from "./pageRedirect/PageRedirectService";
import ReCaptchaV3 from "./runtimeComponent/captcha/CaptchComponent";
import DataUtils, { cleanResumeData, isStorageExists, populateProgramContainer, resumeFormValues } from "./utilities/DataUtils";
import SessionStorageService from "./services/SessionStorageService";
import { setBaseHeaders, setBasePayload, checkSelectedService, checkUserProf } from "./utilities/submitUtils";
import TrackPageView from "./utilities/TrackPageView";
import { getEnvVar } from "./config/RuntimeConfig";
import { copySimpleNonEmptyAttributes } from "./utilities/URLUtils";

interface Props {
	isEng: boolean;
	programContainer: ProgramContainer;
	stepperIndex: number;
	callbackStepperIndex: (stepperIndex: number) => void;
}

interface State {
	programContainer: ProgramContainer
	isEng: boolean
	isBusy: boolean
	stepperIndex: number
}

/**
 * This runtime class handle requests from projects that requires page-redirection in some scenarios.
 */
export class RuntimeWithPageRedirect extends React.Component<Props, State> {
	constructor(props) {
		super(props);
		const profile = this.props.programContainer.Profile;
		this.state = {
			isEng: profile? (profile.lang !== 'fr')  : this.props.isEng,
			programContainer: null,
			isBusy: true,
			stepperIndex: this.props.stepperIndex || 0
		}
		
	}

	track = () => {
		const isEng = this.props.isEng;
		let urlString = window.location.href
		if (!isEng && urlString.indexOf("/fr/")==-1) {
			urlString = urlString.replace("/on-form/", "/on-form/fr/");
		}

		// const previousUrl = window.location.href;
		sessionStorage.setItem('previousUrl', '');

		// Update the browser's address bar without reloading the page
		window.history.pushState({}, '', urlString);

		TrackPageView(this.props.programContainer);
	}

	async componentDidMount() {
		let stepperIndex = this.state.stepperIndex;
		let programContainer: ProgramContainer = this.props.programContainer;
		let lang = this.props.programContainer.Profile?.lang;

		if (isRedirectionRequired(this.props.programContainer.CategoryId)) {
			if (await isStorageExists(this.props.programContainer.CategoryId)) {  // To avoid duplicate service-calls if user refreshes the page.
				const data = await resumeFormValues(this.props.programContainer);
				programContainer = data?.programContainer ?? this.props.programContainer;
				this.track();
				// Redirect based on the transaction status.
				if (isProcessSuccessful(this.props.programContainer.CategoryId)) {
					const apiSuccess = await this.invokeApi(this.props.programContainer);  // Invoke API call (if any)
					if (apiSuccess) {
						stepperIndex = getPageForSuccessfulProcess(this.props.programContainer.CategoryId);
						const displaySection = this.props.programContainer?.ListOfFormSections[stepperIndex];
						console.log('Successful payment transaction, redirect to ' + (this.state.isEng ? displaySection?.ENUTitle : displaySection?.FRATitle) + ' page.');
					} else {
						stepperIndex = this.handleSubmitError();
					}
				} else {
					const apiSuccess = await this.invokeApi(this.props.programContainer);
					if (apiSuccess) {
						stepperIndex = getPageForUnsuccessfulProcess(this.props.programContainer.CategoryId);
						const displaySection = this.props.programContainer?.ListOfFormSections[stepperIndex];
						console.log('Failed payment transaction, redirect to ' + (this.state.isEng ? displaySection?.ENUTitle : displaySection?.FRATitle) + ' page.');
					} else {
						stepperIndex = this.handleSubmitError();
					}
				}
				lang = data?.lang ?? this.props.programContainer.Profile?.lang;
				cleanResumeData(programContainer.CategoryId);
			} else {  // For redirection that the data is repopulated from database.
				const api = programContainer.PageRedirection.api;
				let headers = null;
                if (this.props.programContainer.AuthRequired === "bps") {
					const bpsAccessToken = SessionStorageService.getBPSToken();
					headers = {
						"Content-Type": "application/json",
						Authorization: `Bearer ${bpsAccessToken}`,
					};
				}
				try {
					let response = await axios.get(api + getTransactionId(programContainer.CategoryId), headers ? { headers } : null);
					if (response) {
						if (response.data.formValue) {
							populateProgramContainer(response.data.formValue, programContainer, false);
							stepperIndex = response.data.formValue.stepperIndex;
						} else {
							// Set Entity and auto-populate form fields using DataUtils.assignFormValue
							programContainer.Entity = response.data;
							this.applyNameSentenceCase(programContainer.Entity);
							DataUtils.assignFormValue(programContainer);
							stepperIndex = getPageForSuccessfulProcess(this.props.programContainer.CategoryId);
							// Make form readonly for Cancelled applications
							if (programContainer.Entity?.items?.Status === 'Cancelled') {
								DataUtils.makeCompoenentReadonly(programContainer);
							}
						}

						// Hide action buttons for read-only application statuses
						const status = response.data.formValue?.Status
							?? response.data.items?.Status
							?? programContainer.Entity?.items?.Status;
						if (status === 'Completed' || status === 'Cancelled') {
							DataUtils.hideButtons(programContainer);
						}
					}
					this.track();
					const displaySection = this.props.programContainer?.ListOfFormSections[stepperIndex];
					console.log('Redirect to ' + (this.state.isEng ? displaySection?.ENUTitle : displaySection?.FRATitle) + ' page.');
					lang = response.data.formValue?.lang ?? this.props.programContainer.Profile?.lang;
				} catch (ex) {
			      console.log(ex);
				}
			}
		} else {
			console.log('No redirect is needed.');
		}
		this.setState({
			isEng: lang ? (lang.toLowerCase() !== 'fr') : this.props.isEng,
			programContainer: programContainer,
			isBusy: false,
			stepperIndex
		});
		
		this.props.callbackStepperIndex(stepperIndex);
	}

	applyNameSentenceCase(item: any): void {
		if (!item) return;
		const toSentenceCase = (s: string | null | undefined): string => {
			if (s == null || typeof s !== 'string') return s ?? '';
			const t = s.trim();
			return t ? t.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '';
		};
		if (typeof item.fullName === 'string' && item.fullName.trim()) item.fullName = toSentenceCase(item.fullName);
		if (typeof item.name === 'string' && item.name.trim()) item.name = toSentenceCase(item.name);
	}

	componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
		if (prevProps.isEng != this.props.isEng && this.state.isEng != this.props.isEng) {
			this.setState({ isEng: this.props.isEng });
		}
	}

	async invokeApi(programContainer: ProgramContainer): Promise<boolean> {
		const api = programContainer.PageRedirection.api;
		if (!api) {
			return true;
		}

		const token = SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token;
		const apiKey: APIKey = (programContainer.PageRedirection.apiKeyKey && programContainer.PageRedirection.apiKeyValue) ? {key: programContainer.ApiKeyKey, value: programContainer.ApiKeyValue} : null;
		const headers = setBaseHeaders('application/json', token, apiKey);
		populateApiRequest(this.props.programContainer.CategoryId, programContainer);
		let entity = DataUtils.collectFormValue(programContainer);
		entity = setBasePayload(entity, this.props.isEng ? "EN" : "FR");
		entity = checkUserProf(entity, programContainer.Profile);
		entity = checkSelectedService(entity);
		if (programContainer.Profile?.userId) {  // Provide PS Id to backend.
			entity['publicSecureId'] = programContainer.Profile?.userId;
		} else if (sessionStorage.getItem("userProfile")) {
			const userProfile = JSON.parse(sessionStorage.getItem("userProfile"));
			entity['publicSecureId'] = userProfile?.userId ?? null;
		}

		this.setState({ isBusy: true });
    	let response;
		switch (programContainer.PageRedirection.SubmissionMethod) {
			case "post":
				response = await axios.post(api, entity, { headers: headers });
				break;
			case "get":
				const queryParams = copySimpleNonEmptyAttributes(entity, {});
				const configs = {
					...{ headers },  // Headers
					...queryParams ? { params: queryParams } : undefined,  // Query params
				};
				response = await axios.get(api, configs);
				break;
			case "put":
				response = await axios.put(api, entity, { headers: headers });
				break;
		}

		if (response.status >= 400) {
			return false;
		} else {
			const pageSeq = this.getRedirectPageOnReponse(response);
			if (pageSeq > 0) {
				return true;
			}			
		}
		return false;
	}

	handleSubmitError(): number {
		if (this.props.programContainer.PageRedirection?.apiErrorRedirectPage) {
			const sequence = Math.max(0, this.props.programContainer.PageRedirection.apiErrorRedirectPage - 1);
			return sequence;
		}
		return 0;
	}

	getRedirectPageOnReponse = (response: any) => {
		const statusCode = this.getSubmitResponseData(response);
		// When 'responseDataName' is provided, match the provided status-code with the value of the field returned from API.
		const pages = this.props.programContainer.PageRedirection?.responseDataName ? 
			this.props.programContainer.PageRedirection.apiSuccessRedirectPage?.filter(page => page.statusCode == statusCode) : 
			this.props.programContainer.PageRedirection.apiSuccessRedirectPage;
		if (pages?.length) {
			return pages[0].redirectPage;
		}
		return null;
	}

	getSubmitResponseData = (response: any) => {
		let responseData = response.data;
		if (response.data && this.props.programContainer.PageRedirection?.responseDataName) {
			responseData = response.data[this.props.programContainer.PageRedirection.responseDataName];
		}
		return responseData;
	}

	FormPreview = () => (
		<FormPreviewComponent
			programContainer={this.state.programContainer}
			isEng={(this.state.isEng ?? true) || this.props.programContainer.EnglishOnly}
			stepperIndex={this.state.stepperIndex}
			onStepChange={(stepperIndex) => {
				this.setState({ stepperIndex });
				this.props.callbackStepperIndex(stepperIndex);
			}}
			callBackLang={isEng => this.setState({ isEng: this.props.programContainer.EnglishOnly || isEng })}
			callback={(programContainer) => { this.setState({ programContainer: programContainer }) }}
			callbackOriginalData={(originalData) => { console.log(originalData) }}/>
	)

	render() {
		if (this.state.isBusy) {
			return <SpinnerComponent isBusy></SpinnerComponent>
		}

		if (this.state.programContainer == null)
				return null;
		return (
			<div>
				{(!getEnvVar('DISABLED_RECAPTCHA') || getEnvVar('DISABLED_RECAPTCHA') == 'false') ? (
					<ReCaptchaV3 lang={this.state.isEng ? 'en' : 'fr'}>
						{this.FormPreview()}
					</ReCaptchaV3>
				) : (
					<div>{this.FormPreview()}</div>
				)}
			</div>
		)
	}
}
